/**
 * Provisional Conversations-list rows for chats the server has not listed
 * yet (SE-8865).
 * @remarks
 * The sidebar listed a conversation only once `SearchMyThreads` returned it,
 * and nothing refetched that query after a chat finished. Refetching alone is
 * not enough either: ask-gemini writes the thread row without awaiting it
 * (`void trackThread(...)` ahead of the end frame), so the end event can reach
 * the browser before the row exists, and a refetch at that moment still
 * misses it.
 *
 * So a finished chat is recorded here as a provisional row, and the
 * `searchMyThreads` field policy below merges these rows into every read of
 * the unfiltered list. Merging at READ time is what lets a row survive a
 * refetch that does not contain it yet: the server's answer replaces the
 * stored list wholesale, but the provisional rows are not in the stored list
 * — they are added each time it is read. Once the server returns the same id,
 * its row wins and the provisional one is no longer shown.
 *
 * ## The id is the server's, never the client's session id
 *
 * The client sends `<org>#<user>#<uuid>`; ask-gemini sanitizes that and lists
 * the thread as `<org>:<user>:<first 26 chars of the uuid>`
 * (`constructThreadId`), announcing the result in the stream's `start` frame.
 * A row keyed by the client's session id is never matched by the server's
 * row, so it is never retired: the list shows the chat twice, and pressing the
 * provisional copy asks the server for a thread id it has never heard of.
 * The row is therefore keyed by the `start` frame's id, and a stream without
 * one records nothing. The sanitizer is deliberately not copied here: the
 * server already says which id it used.
 */

import { makeVar } from "@apollo/client";
import type { FieldPolicy, Reference, StoreObject } from "@apollo/client";
import type { IMessage } from "react-native-gifted-chat";

/** A sidebar row for a finished chat the server has not listed yet. */
export interface ProvisionalThread {
  readonly __typename: "UserThread";
  readonly id: string;
  readonly title: string;
  readonly firstQuestion: string;
  readonly lastAnswer: string;
  readonly lastAccessed: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly messageCount: number;
  readonly status: string;
  readonly isFavorite: boolean;
  /**
   * The chat's messages, to reopen it before the server can return them.
   * Never part of the list row.
   */
  readonly transcript: readonly IMessage[];
}

/** An edge as the cache stores it: the node is a normalized reference. */
interface ThreadEdge {
  readonly __typename?: string;
  readonly node: Reference | StoreObject;
}

/** The stored `searchMyThreads` connection. */
interface ThreadConnection {
  readonly __typename?: string;
  readonly edges?: readonly ThreadEdge[];
}

/** Characters of the first question ask-gemini keeps as the thread title. */
const TITLE_LENGTH = 100;

/**
 * Finished chats the server has not listed yet, newest first.
 * @remarks Read by the `searchMyThreads` field policy, so changing it
 * re-renders every watcher of the unfiltered thread list.
 */
export const provisionalThreadsVar = makeVar<readonly ProvisionalThread[]>([]);

/**
 * Whether a message is a real chat turn from the given side.
 * @param message - The chat message
 * @param side - `user` for a question, `ai` for an answer
 * @returns True for a non-system message from that side with text
 */
const isTurnFrom = (message: IMessage, side: "user" | "ai"): boolean =>
  message.user._id === side && !message.system && message.text.trim() !== "";

/**
 * Builds the provisional row for a finished chat.
 * @param threadId - The id the server lists the thread under, from the
 * stream's `start` frame (not the client's session id)
 * @param messages - The chat's messages, oldest first
 * @param now - ISO timestamp for the row's access and creation times
 * @returns The row, or null unless the chat has both a question and an answer
 */
export const buildProvisionalThread = (
  threadId: string,
  messages: readonly IMessage[],
  now: string
): ProvisionalThread | null => {
  const question = messages.find(message => isTurnFrom(message, "user"));
  const answer = messages.filter(message => isTurnFrom(message, "ai")).at(-1);
  if (!threadId || !question || !answer) return null;

  const firstQuestion = question.text.trim();
  return {
    __typename: "UserThread",
    id: threadId,
    title: firstQuestion.substring(0, TITLE_LENGTH),
    firstQuestion,
    lastAnswer: answer.text,
    lastAccessed: now,
    createdAt: now,
    updatedAt: now,
    // The server's row reads 0 (ask-gemini writes `turn_count`, not
    // `message_count`); matching it keeps the row from changing from
    // "2 messages" to "No messages" when the server's row replaces it.
    messageCount: 0,
    status: "active",
    isFavorite: false,
    transcript: messages,
  };
};

/**
 * Records a provisional row, replacing any earlier one for the same chat.
 * @param rows - The current provisional rows, newest first
 * @param row - The row to record
 * @returns The rows with `row` first
 */
export const upsertProvisionalThread = (
  rows: readonly ProvisionalThread[],
  row: ProvisionalThread
): readonly ProvisionalThread[] => [
  row,
  ...rows.filter(existing => existing.id !== row.id),
];

/**
 * Drops provisional rows the server now lists.
 * @param rows - The current provisional rows
 * @param serverIds - Thread ids the server returned
 * @returns The rows the server has not listed yet
 */
export const pruneProvisionalThreads = (
  rows: readonly ProvisionalThread[],
  serverIds: readonly string[]
): readonly ProvisionalThread[] => {
  const listed = new Set(serverIds);
  return rows.filter(row => !listed.has(row.id));
};

/**
 * The list row for a provisional thread: every field but its transcript.
 * @param row - The provisional thread
 * @param row.transcript - Its messages, left out of the list row
 * @returns The node the list renders
 */
const listedNode = ({
  transcript: _transcript,
  ...node
}: ProvisionalThread): Omit<ProvisionalThread, "transcript"> => node;

/**
 * Adds the provisional rows the server has not listed to the server's rows.
 * @param serverEdges - The server's edges
 * @param rows - The provisional rows, newest first
 * @param idOf - Reads an edge's thread id
 * @returns The merged edges; a server row always wins over a provisional one
 * @remarks Order here is not display order: the sidebar sorts by
 * `lastAccessed`. A provisional row's `lastAccessed` is the CLIENT's clock at
 * completion, while server rows carry the server's, so a client clock running
 * well behind the server's can sort a just-finished chat below rows the
 * server touched more recently.
 */
export const mergeProvisionalThreads = <TEdge extends ThreadEdge>(
  serverEdges: readonly TEdge[],
  rows: readonly ProvisionalThread[],
  idOf: (edge: TEdge) => string | undefined
): readonly (TEdge | ThreadEdge)[] => {
  const unlisted = pruneProvisionalThreads(
    rows,
    serverEdges.map(idOf).filter((id): id is string => id !== undefined)
  );
  return [
    ...unlisted.map(row => ({
      __typename: "UserThreadEdge",
      node: listedNode(row),
    })),
    ...serverEdges,
  ];
};

/**
 * Thread ids in a stored connection.
 * @param connection - The connection
 * @param idOf - Reads an edge's thread id
 * @returns The ids it lists
 */
const idsIn = (
  connection: ThreadConnection,
  idOf: (edge: ThreadEdge) => string | undefined
): string[] =>
  (connection.edges ?? [])
    .map(idOf)
    .filter((id): id is string => id !== undefined);

/**
 * Field policy for `Query.searchMyThreads`: adds the provisional rows to the
 * unfiltered list, and retires each one the server lists.
 * @remarks
 * `read` takes the variable before any early return so every read registers
 * it as a dependency; a later change then re-renders the list. A search
 * (`query` set) is the server's filtered answer and is returned untouched.
 *
 * Retiring happens in `merge` because that is the one place that sees the
 * server's own answer. A refetch's RESULT is read back through `read`, so it
 * already contains the provisional rows and cannot tell which ones the server
 * returned.
 */
export const SEARCH_MY_THREADS_FIELD_POLICY: FieldPolicy<ThreadConnection> = {
  read(existing, { args, readField }) {
    const rows = provisionalThreadsVar();
    if (!existing || args?.query || rows.length === 0) return existing;
    return {
      ...existing,
      edges: mergeProvisionalThreads(existing.edges ?? [], rows, edge =>
        readField<string>("id", edge.node)
      ),
    };
  },
  merge(_existing, incoming, { readField }) {
    const rows = provisionalThreadsVar();
    const unlisted = pruneProvisionalThreads(
      rows,
      idsIn(incoming, edge => readField<string>("id", edge.node))
    );
    if (unlisted.length !== rows.length) provisionalThreadsVar(unlisted);
    return incoming;
  },
};
